import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { SurveyStatus } from '@ps/survey-kernel';
import {
  SurveySort,
  ChildSurveyFilter,
  SurveyChannelCreationConfirmedEvent,
  SurveyTabStatus,
  CombineSurveyTrafficReportParams,
  SurveyStatusParam,
} from '@ps/survey-utils';

import {
  SurveyListActions,
  SurveyListInitParams,
} from './survey-list/survey-list.actions';
import { SurveyStateFilters } from './survey-state.model';
import { SurveyReferenceActions } from './reference/survey-reference.actions';
import {
  ManageSurveyActions,
  ManageSurveySocketActions,
} from './manage-survey/manage-survey.actions';

@Injectable({ providedIn: 'root' })
export class SurveyActionsFacade {
  private readonly _store = inject(Store);

  public init(params: SurveyListInitParams): void {
    this._store.dispatch(SurveyListActions.init({ ...params }));
  }

  public teardown(): void {
    this._store.dispatch(SurveyListActions.teardown());
  }

  public setFilters(filters: Partial<SurveyStateFilters>): void {
    this._store.dispatch(SurveyListActions.setFilters({ filters }));
  }

  public setTab(tabId: SurveyTabStatus, status: SurveyStatusParam): void {
    this._store.dispatch(SurveyListActions.setTab({ tabId, status }));
  }

  public cancelLoad(): void {
    this._store.dispatch(SurveyListActions.cancelLoad());
  }

  public setSort(sort: SurveySort): void {
    this._store.dispatch(SurveyListActions.setSort({ sort }));
  }

  public loadNextPage(): void {
    this._store.dispatch(SurveyListActions.loadNextPage());
  }

  public reload(): void {
    this._store.dispatch(SurveyListActions.reload());
  }

  public loadChildSurveys(parentId: number, filter: ChildSurveyFilter): void {
    this._store.dispatch(
      SurveyListActions.loadChildSurveys({ parentId, filter }),
    );
  }

  public updateSurveyStatus(surveyId: number, status: SurveyStatus): void {
    this._store.dispatch(
      SurveyListActions.updateSurveyStatus({ surveyId, status }),
    );
  }

  // Survey Preview actions
  public loadSurveyDetails(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadSurveyDetails({ surveyId }));
  }

  public loadFullSurveyDetails(surveyId: number): void {
    this._store.dispatch(
      ManageSurveyActions.loadFullSurveyDetails({ surveyId }),
    );
  }

  public loadChildSurveyDetails(surveyId: number): void {
    this._store.dispatch(
      ManageSurveyActions.loadChildSurveyDetails({ surveyId }),
    );
  }

  public resetSurveyDetails(): void {
    this._store.dispatch(ManageSurveyActions.resetSurveyDetails());
  }

  public loadSurveyHealth(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadSurveyHealth({ surveyId }));
  }

  public loadRemainingHours(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadRemainingHours({ surveyId }));
  }

  public loadActivityLogs(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadActivityLogs({ surveyId }));
  }

  public resetActivityLogs(): void {
    this._store.dispatch(ManageSurveyActions.resetActivityLogs());
  }

  public loadComments(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadComments({ surveyId }));
  }

  public saveCrmId(surveyId: number, crmId: string): void {
    this._store.dispatch(ManageSurveyActions.saveCrmId({ surveyId, crmId }));
  }

  public saveAsanaId(surveyId: number, asanaId: string): void {
    this._store.dispatch(
      ManageSurveyActions.saveAsanaId({ surveyId, asanaId }),
    );
  }

  public saveBillingNumber(surveyId: number, billingNumber: string): void {
    this._store.dispatch(
      ManageSurveyActions.saveBillingNumber({ surveyId, billingNumber }),
    );
  }

  public saveCustomRedirectField(surveyId: number, value: string): void {
    this._store.dispatch(
      ManageSurveyActions.saveCustomRedirectField({ surveyId, value }),
    );
  }

  public updateCurrentCpi(
    surveyId: number,
    cpi: number,
    isChild = false,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.updateCurrentCpi({ surveyId, cpi, isChild }),
    );
  }

  public loadSurveyQuotas(surveyId: number, page = 1): void {
    this._store.dispatch(
      ManageSurveyActions.loadSurveyQuotas({ surveyId, page }),
    );
  }

  public loadMoreQuotas(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadMoreQuotas({ surveyId }));
  }

  public loadAllQuotas(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadAllQuotas({ surveyId }));
  }

  public resetSurveyQuotas(): void {
    this._store.dispatch(ManageSurveyActions.resetSurveyQuotas());
  }

  public setQuotaLock(
    surveyId: number,
    psQuotaId: string,
    locked: boolean,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaLock({ surveyId, psQuotaId, locked }),
    );
  }

  public setQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    currentTarget: number,
    updateSupplier: boolean,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaCurrentTarget({
        surveyId,
        psQuotaId,
        currentTarget,
        updateSupplier,
      }),
    );
  }

  public loadTrafficChannels(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadTrafficChannels({ surveyId }));
  }

  public resetTrafficChannels(): void {
    this._store.dispatch(ManageSurveyActions.resetTrafficChannels());
  }

  public setTrafficChannelStatus(surveyId: number, status: SurveyStatus): void {
    this._store.dispatch(
      ManageSurveyActions.setTrafficChannelStatus({ surveyId, status }),
    );
  }

  public launchTrafficChannel(
    surveyId: number,
    event: SurveyChannelCreationConfirmedEvent,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.launchTrafficChannel({ surveyId, event }),
    );
  }

  public downloadRespondentListTemplate(): void {
    this._store.dispatch(ManageSurveyActions.downloadRespondentListTemplate());
  }

  public uploadPsidFile(surveyId: number, file: File): void {
    this._store.dispatch(
      ManageSurveyActions.uploadPsidFile({ surveyId, file }),
    );
  }

  public clearPsidFile(): void {
    this._store.dispatch(ManageSurveyActions.clearPsidFile());
  }

  // Reference actions
  public loadBuyerManagers(companyId: number): void {
    this._store.dispatch(
      SurveyReferenceActions.loadBuyerManagers({ companyId }),
    );
  }

  public loadOperatorManagers(): void {
    this._store.dispatch(SurveyReferenceActions.loadOperatorManagers());
  }

  public loadTransactionStatuses(): void {
    this._store.dispatch(SurveyReferenceActions.loadTransactionStatuses());
  }

  public loadBuyerSettings(buyerId: number): void {
    this._store.dispatch(SurveyReferenceActions.loadBuyerSettings({ buyerId }));
  }

  public downloadCombinedSTReport(
    data: CombineSurveyTrafficReportParams,
  ): void {
    this._store.dispatch(
      SurveyReferenceActions.downloadCombinedSTReport({ ...data }),
    );
  }

  public downloadCsReport(surveyId: number): void {
    this._store.dispatch(SurveyReferenceActions.downloadCsReport({ surveyId }));
  }

  public joinSurveyRoom(surveyId: number): void {
    this._store.dispatch(
      ManageSurveySocketActions.joinSurveyRoom({ surveyId }),
    );
  }

  public leaveSurveyRoom(surveyId: number): void {
    this._store.dispatch(
      ManageSurveySocketActions.leaveSurveyRoom({ surveyId }),
    );
  }

  public joinQuotaTab(surveyId: number): void {
    this._store.dispatch(ManageSurveySocketActions.joinQuotaTab({ surveyId }));
  }

  public leaveQuotaTab(surveyId: number): void {
    this._store.dispatch(ManageSurveySocketActions.leaveQuotaTab({ surveyId }));
  }

  public joinSupplierTab(surveyId: number): void {
    this._store.dispatch(
      ManageSurveySocketActions.joinSupplierTab({ surveyId }),
    );
  }

  public leaveSupplierTab(surveyId: number): void {
    this._store.dispatch(
      ManageSurveySocketActions.leaveSupplierTab({ surveyId }),
    );
  }

  public joinTCTab(surveyId: number): void {
    this._store.dispatch(ManageSurveySocketActions.joinTCTab({ surveyId }));
  }

  public leaveTCTab(surveyId: number): void {
    this._store.dispatch(ManageSurveySocketActions.leaveTCTab({ surveyId }));
  }

  public subscribeTrafficSurveys(surveyIds: number[]): void {
    this._store.dispatch(
      ManageSurveySocketActions.subscribeTrafficSurveys({ surveyIds }),
    );
  }

  public unsubscribeTrafficSurveys(surveyIds: number[]): void {
    this._store.dispatch(
      ManageSurveySocketActions.unsubscribeTrafficSurveys({ surveyIds }),
    );
  }
}
